/**
 * EmailJS service for sending emails
 *
 * SETUP INSTRUCTIONS:
 * 1. Go to https://www.emailjs.com/ and create a free account
 * 2. Create an email service (Gmail, Outlook, etc.)
 * 3. Create an email template with placeholders like {{name}}, {{email}}, etc.
 * 4. Get your Public Key, Service ID, and Template ID
 * 5. Set them as environment variables or update the constants below
 */

import emailjs from "@emailjs/browser";

// EmailJS configuration
// Set these as environment variables in .env.local file
// Required: REACT_APP_EMAILJS_PUBLIC_KEY, REACT_APP_EMAILJS_SERVICE_ID, REACT_APP_EMAILJS_TEMPLATE_ID
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "";
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || "";
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "";

/**
 * Initialize EmailJS with your public key
 */
export const initEmailJS = () => {
  if (EMAILJS_PUBLIC_KEY) {
    emailjs.init(EMAILJS_PUBLIC_KEY);
  }
};

/**
 * Send RSVP email using EmailJS
 * @param {Object} formData - RSVP form data
 * @param {string} formData.name - Guest name
 * @param {string} formData.email - Guest email
 * @param {string} formData.phone - Guest phone
 * @param {string} formData.areYouComing - 'yes' or 'no'
 * @param {boolean} formData.withAttendant - Whether coming with attendant
 * @param {string} formData.attendantName - Attendant name (if applicable)
 * @param {string} formData.message - Message from guest
 * @param {string} formData.hasFoodSensitivity - 'yes' or 'no' (only if coming)
 * @param {string} formData.foodSensitivityDetails - Details if hasFoodSensitivity is 'yes'
 * @param {string} formData.accommodationPlan - 'travel_home' | 'self_arrange' (only if coming)
 * @returns {Promise} Promise that resolves when email is sent
 */
export const sendRSVPEmail = async (formData) => {
  try {
    if (!EMAILJS_PUBLIC_KEY || !EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID) {
      throw new Error(
        "EmailJS configuration is missing. Please set environment variables."
      );
    }

    // Prepare template parameters matching the Hungarian template
    // Template variables: {{name}}, {{email}}, {{phone}}, {{response}}, {{is_partner_coming}}, {{partner_name}}, {{message}}, {{site_name}}
    const accommodationHu =
      formData.accommodationPlan === "travel_home"
        ? "Hazautazom, nem foglalok szállást"
        : formData.accommodationPlan === "self_arrange"
          ? "Saját magam intézem a szállást"
          : "";

    const extraLines = [];
    if (formData.areYouComing === "yes") {
      if (formData.hasFoodSensitivity) {
        extraLines.push(
          `Ételérzékenység: ${formData.hasFoodSensitivity === "yes" ? "Igen" : "Nem"}`
        );
      }
      if (formData.hasFoodSensitivity === "yes" && formData.foodSensitivityDetails) {
        extraLines.push(`Ételérzékenység részletei: ${formData.foodSensitivityDetails}`);
      }
      if (accommodationHu) {
        extraLines.push(`Szállás: ${accommodationHu}`);
      }
    }

    const combinedMessage =
      [formData.message || "", extraLines.length ? "\n---\n" + extraLines.join("\n") : ""]
        .join("\n")
        .trim();

    const templateParams = {
      name: formData.name || "",
      email: formData.email || "",
      phone: formData.phone || "",
      response: formData.areYouComing === "yes" ? "Igen" : "Nem",
      is_partner_coming: formData.withAttendant ? "Igen" : "Nem",
      partner_name: formData.attendantName || "",
      message: combinedMessage,
      site_name: "Zsófi és Peti esküvője", // Wedding site name
    };

    // Send email
    const response = await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      templateParams
    );

    return {
      success: true,
      message: "Email sent successfully",
      response,
    };
  } catch (error) {
    console.error("EmailJS error:", error);
    return {
      success: false,
      message: error.text || error.message || "Failed to send email",
      error,
    };
  }
};
